import { forwardRef, useImperativeHandle, useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import {
  Phone,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Pencil,
  ArrowRight,
  LockKeyhole,
} from "lucide-react";
import DateDisplay from "components/common/DateDisplay";
import { DelegationTask } from "protogen/delegations_pb";
import BlockContainer from "./BlockContainer";
import BlockGutterMap from "./BlockGutterMap";
import { BlockHandle } from "./utils";
import LinkRouter from "components/navigation/LinkRouter";
import FlexBox from "components/common/FlexBox";
import { useNavigate } from "react-router-dom";
import { useScrollOnExpand } from "components/hooks/useScrollOnExpand";
import useIsMobile from "components/hooks/useIsMobile";
import { SecretRevealConfirmation } from "components/secrets/SecretRevealConfirmation";
import { Family } from "protogen/advisors_service_pb";
import {
  isPhoneDetail,
  parseDetails,
  separateDetailsByType,
} from "components/delegation/DelegationDetails";

interface DelegationBlockProps {
  delegationTask: DelegationTask;
  family?: Family;
}

const DelegationBlock = forwardRef<BlockHandle, DelegationBlockProps>(
  ({ delegationTask, family }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [revealSecretRef, setRevealSecretRef] = useState<string | null>(null);
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { blockRef: scrollRef, handleExpandWithScroll } = useScrollOnExpand();

    useImperativeHandle(ref, () => ({
      isPolling: () => false,
    }));

    const status = delegationTask.status;

    const details = parseDetails(delegationTask.detailsJson);
    const { normal: normalDetails, secret: secretDetails } =
      separateDetailsByType(details);

    const currentStatusComment = [...(delegationTask.comments || [])]
      .reverse()
      .find(
        (comment) =>
          comment.updatedStatus === status &&
          (comment.textContent || comment.content),
      );

    const hasExpandableContent =
      details.length > 0 || delegationTask.description;

    const commentsCount = (
      <Box alignItems="center" gap="4px" display="flex" flexDirection="row">
        <MessageSquare size={16} color="#8E9598" />
        <Typography variant="bodySmall" color="text.secondary">
          {
            delegationTask.comments.filter((c) => c.textContent || c.content)
              .length
          }
        </Typography>
      </Box>
    );

    return (
      <BlockContainer
        ref={scrollRef}
        gutterIcon={BlockGutterMap.delegation.icon}
      >
        <Box sx={{ padding: "20px" }}>
          <FlexBox direction="column" gap="0px">
            <FlexBox justifyContent="space-between" alignItems="center">
              <Typography
                variant="caption"
                sx={{
                  color: "#198282",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                LIGHTNING TASK
              </Typography>
              {hasExpandableContent && (
                <Box
                  onClick={() => {
                    if (!isExpanded) {
                      handleExpandWithScroll(() => setIsExpanded(true));
                    } else {
                      setIsExpanded(false);
                    }
                  }}
                  sx={{ cursor: "pointer", display: "flex" }}
                >
                  {isExpanded ? (
                    <ChevronUp size={20} stroke="#8E9598" />
                  ) : (
                    <ChevronDown size={20} stroke="#8E9598" />
                  )}
                </Box>
              )}
            </FlexBox>

            <LinkRouter
              to={`/tasks/subtasks/${encodeURIComponent(delegationTask.ref)}`}
              sx={{
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <Typography variant="bodyHeavy">
                {delegationTask.title}
              </Typography>
            </LinkRouter>

            <FlexBox
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              sx={{ flexDirection: isMobile ? "column" : "row" }}
            >
              <FlexBox alignItems="center" gap="8px" sx={{ flex: 1 }}>
                {!isMobile && commentsCount}
                {delegationTask.comments.length > 0 && (
                  <Typography variant="bodySmall" color="text.secondary">
                    {!isMobile ? " • " : ""}Last updated{" "}
                    <DateDisplay
                      date={
                        new Date(
                          Number(
                            delegationTask.comments[
                              delegationTask.comments.length - 1
                            ].createdSec,
                          ) * 1000,
                        )
                      }
                      sx={{ display: "inline" }}
                    />
                  </Typography>
                )}
              </FlexBox>
              <FlexBox
                alignItems="center"
                gap="8px"
                sx={{
                  flexShrink: 0,
                  justifyContent: "flex-end",
                  width: isMobile ? "100%" : undefined,
                }}
              >
                <FlexBox
                  alignItems="center"
                  gap="8px"
                  sx={{ justifyContent: isMobile ? "flex-start" : "flex-end" }}
                >
                  <Chip
                    label={
                      status === "completed"
                        ? "Done"
                        : status === "in_progress" || status === "delegated"
                          ? "In Progress"
                          : status === "queued"
                            ? "Queued"
                            : status === "cancelled"
                              ? "Cancelled"
                              : "Pending"
                    }
                    variant={
                      status === "completed"
                        ? "success"
                        : status === "in_progress" || status === "delegated"
                          ? "warning"
                          : "gray"
                    }
                    size="small"
                    sx={{ height: "24px", fontSize: "12px" }}
                  />
                  {isMobile && commentsCount}
                </FlexBox>
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(
                      `/tasks/subtasks/${encodeURIComponent(
                        delegationTask.ref,
                      )}`,
                    )
                  }
                  sx={{
                    border: "1px solid #D4D4D4",
                    borderRadius: "100px",
                    padding: "8px",
                    color: "text.tertiary",
                  }}
                >
                  <ArrowRight size={16} />
                </IconButton>
              </FlexBox>
            </FlexBox>
          </FlexBox>

          {currentStatusComment && (
            <Box
              sx={{
                marginTop: "12px",
                padding: "12px 12px 2px 12px",
                backgroundColor: "gray.2",
                borderRadius: "8px",
              }}
            >
              <Typography variant="bodySmall">
                {currentStatusComment.textContent ||
                  currentStatusComment.content}
              </Typography>
              {currentStatusComment.author && (
                <FlexBox
                  alignItems="center"
                  gap="4px"
                  sx={{ marginBottom: "4px" }}
                >
                  {currentStatusComment.author.profilePath ? (
                    <LinkRouter
                      to={currentStatusComment.author.profilePath}
                      useColorScheme={true}
                    >
                      {"- "}
                      {currentStatusComment.author.displayName}
                    </LinkRouter>
                  ) : (
                    <Typography variant="bodySmallHeavy">
                      {"- "}
                      {currentStatusComment.author.displayName}
                    </Typography>
                  )}
                </FlexBox>
              )}
            </Box>
          )}

          <Collapse in={isExpanded}>
            {normalDetails.length > 0 && (
              <FlexBox direction="column" gap="8px" sx={{ marginTop: "16px" }}>
                {normalDetails.map((detail, index) => (
                  <FlexBox key={index} alignItems="center" gap="8px">
                    <Typography
                      variant="bodySmallHeavy"
                      sx={{ minWidth: "120px" }}
                    >
                      {detail.name}
                    </Typography>
                    {isPhoneDetail(detail) && (
                      <Phone size={14} color="#198282" />
                    )}
                    <Typography
                      variant="bodySmall"
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      {detail.value}
                    </Typography>
                  </FlexBox>
                ))}
              </FlexBox>
            )}

            {secretDetails.length > 0 && (
              <FlexBox direction="column" gap="8px" sx={{ marginTop: "16px" }}>
                <Typography variant="bodyHeavy">Secrets</Typography>
                {secretDetails.map((detail, index) => (
                  <FlexBox key={index} alignItems="center" gap="8px">
                    <Typography
                      variant="bodySmallHeavy"
                      sx={{ minWidth: "120px" }}
                    >
                      {detail.name}
                    </Typography>
                    <Typography
                      variant="bodySmall"
                      sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
                    >
                      ••••••••
                    </Typography>
                    {detail.value && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRevealSecretRef(detail.value);
                        }}
                        sx={{
                          padding: "2px",
                          color: "primary.main",
                        }}
                      >
                        <LockKeyhole size={16} />
                      </IconButton>
                    )}
                  </FlexBox>
                ))}
              </FlexBox>
            )}

            {delegationTask.description && (
              <Box
                sx={{
                  marginTop: "16px",
                  padding: "12px",
                  backgroundColor: "#F8F9FA",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="bodySmall"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {delegationTask.description}
                </Typography>
              </Box>
            )}

            <FlexBox justifyContent="flex-end" sx={{ marginTop: "16px" }}>
              <Button
                variant="text"
                size="small"
                startIcon={<Pencil size={16} />}
                onClick={() =>
                  navigate(
                    `/tasks/subtasks/${encodeURIComponent(
                      delegationTask.ref,
                    )}?comment=1`,
                  )
                }
              >
                Update details
              </Button>
            </FlexBox>
          </Collapse>
        </Box>
        {revealSecretRef && (
          <SecretRevealConfirmation
            open={!!revealSecretRef}
            onClose={() => setRevealSecretRef(null)}
            secretRef={revealSecretRef}
            family={family}
          />
        )}
      </BlockContainer>
    );
  },
);

DelegationBlock.displayName = "DelegationBlock";

export default DelegationBlock;
